/**
 * You › Machines › load (G1 / V21): live CPU/memory/GPU/disk/thermal per machine, why an
 * overloaded machine is deferring new runs, per-run usage, a capacity recommendation with its
 * evidence, and a drillable week of history (day → hour → 15-minute bucket).
 * Everything comes from the machine's end-to-end encrypted `machine.load` report.
 */
import { Link } from '@tanstack/react-router'
import type { LoadBucket, LoadSample, MachineLoadFrame } from '@viko/protocol'
import { CircleAlert } from 'lucide-react'
import {
  groupBuckets,
  type LoadGroup,
  overLabel,
  recommendationsFor,
  useMachineLoad,
} from '../lib/machine-load.js'
import { Ago, Meter } from './ui.js'

const pct = (n: number) => `${Math.round(n)}%`
const THERMAL = ['Nominal', 'Fair', 'Serious', 'Critical']
const day = (t: number) =>
  new Date(t).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
const clock = (t: number) =>
  new Date(t).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })

export function MachineLoad({ machineId, name }: { machineId: string; name: string }) {
  const q = useMachineLoad()
  const all = q.data ?? []
  const mine = all.find((x) => x.machineId === machineId)?.report
  if (!mine) return null
  const peers = all.filter((x) => x.machineId !== machineId).map((x) => x.report)
  return <Loaded frame={mine} peers={peers} name={name} />
}

function Loaded({
  frame,
  peers,
  name,
}: {
  frame: MachineLoadFrame
  peers: MachineLoadFrame[]
  name: string
}) {
  const s = frame.sample
  const recs = recommendationsFor(frame, peers)
  return (
    <div className="grid gap-3" data-testid="machine-load">
      <div className="flex items-baseline justify-between text-[14px] text-text-2">
        <span className="font-medium">Load</span>
        <span className="text-text-3">
          sampled <Ago ts={s.at} />
        </span>
      </div>
      {frame.overloaded ? (
        <div
          role="status"
          className="flex items-start gap-2 rounded-lg bg-surface-2 p-3 text-[14px] text-status-wait"
        >
          <CircleAlert size={16} aria-hidden="true" className="mt-0.5 shrink-0" />
          <span>
            <strong>Overloaded: new runs are deferred to other machines.</strong>{' '}
            {frame.reasons.join('; ')}
            {frame.overloadedSince ? ` (since ${clock(frame.overloadedSince)})` : ''}. Runs already
            in progress keep going.
          </span>
        </div>
      ) : null}
      <Gauges s={s} name={name} t={frame.thresholds} />
      {recs.map((r) => (
        <details key={r.resource} className="rounded-lg bg-surface-2 p-3 text-[14px]" open>
          <summary className="cursor-pointer font-medium">
            {r.kind === 'upgrade' ? 'Capacity recommendation' : 'Rebalance recommendation'}:{' '}
            {r.resource === 'thermal' ? 'thermal' : r.resource.toUpperCase()}
          </summary>
          <p className="m-0 mt-2 text-text-2">{r.text}</p>
          <dl className="m-0 mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-text-2">
            <dt>Over the limit</dt>
            <dd className="m-0 whitespace-nowrap">{r.overPct}% of samples</dd>
            <dt>Peak / average</dt>
            <dd className="m-0 whitespace-nowrap">
              {r.peak} / {r.avg}
            </dd>
            <dt>Window</dt>
            <dd className="m-0 whitespace-nowrap">
              {r.windowHours} h ({r.buckets} buckets)
            </dd>
          </dl>
        </details>
      ))}
      {frame.runs.length ? (
        <details className="text-[14px]">
          <summary className="cursor-pointer font-medium text-text-2">
            Runs on this machine ({frame.runs.length})
          </summary>
          <ul className="m-0 mt-2 grid list-none gap-1.5 p-0">
            {[...frame.runs]
              .sort((a, b) => (b.cpuPct ?? 0) - (a.cpuPct ?? 0))
              .map((r) => (
                <li key={r.runId} className="flex items-baseline justify-between gap-3">
                  <Link to="/s/$runId" params={{ runId: r.runId }} className="underline">
                    Run {r.runId.slice(0, 8)}
                  </Link>
                  <span className="whitespace-nowrap text-text-2">
                    {r.cpuPct !== undefined ? `${r.cpuPct}% CPU · ` : ''}
                    {r.rssMb} MB · {r.procs} process{r.procs === 1 ? '' : 'es'}
                  </span>
                </li>
              ))}
          </ul>
        </details>
      ) : null}
      <History frame={frame} name={name} />
    </div>
  )
}

function Gauges({
  s,
  name,
  t,
}: {
  s: LoadSample
  name: string
  t: MachineLoadFrame['thresholds']
}) {
  const rows: { label: string; value: number; limit: number; text: string }[] = [
    { label: 'CPU', value: s.cpuPct, limit: t.cpuPct, text: pct(s.cpuPct) },
    { label: 'Memory', value: s.memPct, limit: t.memPct, text: pct(s.memPct) },
    ...(s.gpuPct !== undefined
      ? [{ label: 'GPU', value: s.gpuPct, limit: t.gpuPct, text: pct(s.gpuPct) }]
      : []),
    ...(s.ioPct !== undefined
      ? [{ label: 'Disk', value: s.ioPct, limit: t.ioPct, text: pct(s.ioPct) }]
      : []),
  ]
  return (
    <div className="grid gap-2">
      {rows.map((r) => (
        <div key={r.label} className="grid gap-1">
          <div className="flex justify-between text-[14px] text-text-2">
            <span>{r.label}</span>
            <span className="whitespace-nowrap">
              {r.text}
              <span className="text-text-3"> of {r.limit}% limit</span>
            </span>
          </div>
          <Meter pct={r.value} label={`${name} ${r.label} load`} />
        </div>
      ))}
      {s.ioMBps !== undefined && s.ioPct === undefined ? (
        <div className="flex justify-between text-[14px] text-text-2">
          <span>Disk throughput</span>
          <span className="whitespace-nowrap">{s.ioMBps} MB/s</span>
        </div>
      ) : null}
      {s.thermal !== undefined ? (
        <div className="flex justify-between text-[14px] text-text-2">
          <span>Thermal</span>
          <span className="whitespace-nowrap">
            {THERMAL[s.thermal]}
            <span className="text-text-3"> (limit: {THERMAL[t.thermal]})</span>
          </span>
        </div>
      ) : null}
    </div>
  )
}

function Trend({ history, name }: { history: LoadBucket[]; name: string }) {
  if (history.length < 2) return null
  const t0 = history[0]!.at
  const span = Math.max(1, history[history.length - 1]!.at - t0)
  const pts = (f: (b: LoadBucket) => number) =>
    history.map((b) => `${((b.at - t0) / span) * 600},${50 - f(b) / 2}`).join(' ')
  return (
    <svg
      viewBox="0 0 600 52"
      preserveAspectRatio="none"
      className="h-14 w-full rounded-lg bg-surface-2"
      role="img"
      aria-label={`${name} CPU and memory over the last week`}
    >
      <title>{`${name}: CPU (solid) and memory (dashed), last week`}</title>
      <polyline
        points={pts((b) => b.memAvg)}
        fill="none"
        stroke="var(--vk-text-3)"
        strokeWidth="1.5"
        strokeDasharray="4 3"
        vectorEffect="non-scaling-stroke"
      />
      <polyline
        points={pts((b) => b.cpuAvg)}
        fill="none"
        stroke="var(--vk-text-1)"
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
      />
      {history
        .filter((b) => b.over > 0)
        .map((b) => (
          <line
            key={b.at}
            x1={((b.at - t0) / span) * 600}
            x2={((b.at - t0) / span) * 600}
            y1="0"
            y2="52"
            stroke="var(--vk-status-wait)"
            strokeWidth="2"
            vectorEffect="non-scaling-stroke"
          />
        ))}
    </svg>
  )
}

function Stats({ g }: { g: LoadGroup }) {
  return (
    <span className="whitespace-nowrap text-[14px] text-text-2">
      CPU {pct(g.cpuAvg)} avg / {pct(g.cpuMax)} peak · Mem {pct(g.memAvg)} / {pct(g.memMax)}
      {g.overPct ? ` · over limit ${g.overPct}%` : ''}
    </span>
  )
}

function History({ frame, name }: { frame: MachineLoadFrame; name: string }) {
  const zone = new Date().getTimezoneOffset() * 60_000
  const days = groupBuckets(frame.history, 'day', -zone)
  if (!days.length) return null
  return (
    <details className="text-[14px]">
      <summary className="cursor-pointer font-medium text-text-2">
        Load history ({days.length} day{days.length === 1 ? '' : 's'})
      </summary>
      <div className="mt-2 grid gap-2">
        <Trend history={frame.history} name={name} />
        {days.map((d) => (
          <details key={d.at} className="rounded-lg bg-surface-2 p-2">
            <summary className="grid cursor-pointer gap-0.5">
              <span className="font-medium">{day(d.at)}</span>
              <Stats g={d} />
            </summary>
            <div className="mt-2 grid gap-1.5 pl-2">
              {d.overBy.length ? (
                <p className="m-0 text-text-2">Over the limit: {overLabel(d.overBy)}</p>
              ) : null}
              {groupBuckets(d.buckets, 'hour', -zone).map((h) => (
                <details key={h.at}>
                  <summary className="cursor-pointer">
                    <span className="mr-2 font-medium">{clock(h.at)}</span>
                    <Stats g={h} />
                  </summary>
                  <ul className="m-0 mt-1 grid list-none gap-1 p-0 pl-3 text-text-2">
                    {h.buckets.map((b) => (
                      <li key={b.at} className="whitespace-nowrap">
                        {clock(b.at)}: CPU {pct(b.cpuAvg)} (peak {pct(b.cpuMax)}) · Mem{' '}
                        {pct(b.memAvg)} (peak {pct(b.memMax)})
                        {b.gpuMax !== undefined ? ` · GPU peak ${pct(b.gpuMax)}` : ''}
                        {b.over ? ` · over: ${overLabel(b.overBy)}` : ''}
                      </li>
                    ))}
                  </ul>
                </details>
              ))}
            </div>
          </details>
        ))}
      </div>
    </details>
  )
}
